import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import useFallEvents, { STATUS_OPTIONS, PERIOD_OPTIONS } from '../../hooks/admin/useFallEvents';
import FallEventsFilterBar from './components/fall-events/FallEventsFilterBar';
import FallEventsTable from './components/fall-events/FallEventsTable';
import FallEventDrawer from './components/fall-events/FallEventDrawer';
import { buildClipUrl } from '../../utils/media';

const FallEventsPage = () => {
  const {
    events,
    rawEvents,
    loading,
    error,
    updating,
    statusFilter,
    periodFilter,
    cameraFilter,
    setStatusFilter,
    setPeriodFilter,
    setCameraFilter,
    handleUpdate,
    setError,
    loadEvents,
    cameraOptions,
  } = useFallEvents();

  const [selectedEvent, setSelectedEvent] = React.useState(null);

  const handleRowSelect = (event) => {
    setSelectedEvent(event);
  };

  const handleDrawerSave = async (eventId, payload) => {
    setError('');
    try {
      const updatedEvent = await handleUpdate(eventId, payload);
      if (updatedEvent?.id) {
        setSelectedEvent(updatedEvent);
      }
    } catch (err) {
      // error state is handled in the hook
    }
  };

  return (
    <Container>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Fall Events</Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={loadEvents} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <FallEventsFilterBar
        statusFilter={statusFilter}
        periodFilter={periodFilter}
        cameraFilter={cameraFilter}
        onStatusChange={setStatusFilter}
        onPeriodChange={setPeriodFilter}
        onCameraChange={setCameraFilter}
        onReset={() => {
          setStatusFilter('ALL');
          setPeriodFilter('30');
          setCameraFilter('all');
        }}
        statusOptions={STATUS_OPTIONS}
        periodOptions={PERIOD_OPTIONS}
        cameraOptions={cameraOptions}
        totalCount={events.length}
      />

      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}>
          <CircularProgress />
        </Box>
      ) : (
        <FallEventsTable events={events} onSelect={handleRowSelect} />
      )}

      {updating && (
        <Box display="flex" alignItems="center" gap={1} mt={2}>
          <CircularProgress size={18} />
          <Typography variant="body2">Saving changes...</Typography>
        </Box>
      )}

      <FallEventDrawer
        open={Boolean(selectedEvent)}
        event={rawEvents.find((evt) => evt.id === selectedEvent?.id)}
        buildClipUrl={buildClipUrl}
        onClose={() => setSelectedEvent(null)}
        onSave={handleDrawerSave}
      />
    </Container>
  );
};

export default FallEventsPage;
